import { useCallback, useRef, useState } from "react";
import { Ikone } from "../Ikone.tsx";

/**
 * Hinweise, die stehen bleiben, bis der Nutzer sie schliesst (Task 4, "Uploads bis 100 MB").
 *
 * Gegenstueck zum Toast (`Toast.tsx`), der nach 2,2 Sekunden von selbst verschwindet und nur eine
 * Meldung gleichzeitig zeigt. Ein Upload, der minutenlang laeuft und dann scheitert, oder eine
 * Auswahl, von der einzelne Dateien abgelehnt wurden, braucht beides nicht: KEIN Zeitablauf, und
 * mehrere Hinweise stapeln sich. `App.tsx` rendert `StehendeHinweise` neben `<Toast>` und reicht
 * `zeigeHinweis` an die Uploadwege (Task 5 bis 7).
 *
 * `zeigeHinweis`/`schliesseHinweis` sind referenzstabil (`useCallback`, ohne Abhaengigkeiten): beide
 * gehen in `App.tsx` durch bis in Handler, die selbst in Abhaengigkeitsarrays stehen - siehe die
 * Begruendung bei `Toast.tsx#useMeldungsAnzeige`.
 */
export interface StehenderHinweis {
  id: number;
  text: string;
}

export function useStehendeHinweise(): {
  hinweise: readonly StehenderHinweis[];
  zeigeHinweis: (text: string) => void;
  schliesseHinweis: (id: number) => void;
} {
  const [hinweise, setHinweise] = useState<readonly StehenderHinweis[]>([]);
  // Eigene Kennung je Hinweis, nicht der Text: derselbe Text kann zweimal erscheinen (zwei Dateien,
  // dieselbe Ursache), und "schliessen" soll genau EINEN davon treffen.
  const naechsteId = useRef(1);

  const zeigeHinweis = useCallback((text: string): void => {
    const id = naechsteId.current++;
    setHinweise((bisher) => [...bisher, { id, text }]);
  }, []);

  const schliesseHinweis = useCallback((id: number): void => {
    setHinweise((bisher) => bisher.filter((hinweis) => hinweis.id !== id));
  }, []);

  return { hinweise, zeigeHinweis, schliesseHinweis };
}

export interface StehendeHinweiseProps {
  hinweise: readonly StehenderHinweis[];
  onSchliessen: (id: number) => void;
}

/**
 * Live-Region: der Container mit `role="status"` ist IMMER gemountet, auch bei leerer Liste; die
 * Hinweise kommen als Inhalt in ihn hinein und tragen kein eigenes `role`. Ein Screenreader sagt nur
 * an, was innerhalb einer schon vorhandenen Region neu erscheint - entstuenden Region und Hinweis im
 * selben Schritt (so die erste Fassung: `null` bei leerer Liste, `role="status"` je Hinweis), bliebe
 * es meist stumm. Dieselbe Konvention und Begruendung wie `LadeHinweis.tsx`; auch hier darf das CSS
 * (`styles/neubau/upload.css`) die leere Region nicht mit `visibility: hidden`/`display: none` aus dem
 * Barrierefreiheitsbaum nehmen - sie ist ohne Inhalt ohnehin zero-size.
 *
 * `aria-atomic="false"`: die Rolle `status` bringt implizit `aria-atomic="true"` mit, ein Screenreader
 * sagte dann bei jedem neu hinzukommenden Hinweis die GANZE Region an - also auch alle noch stehenden
 * noch einmal (sie bleiben, bis der Nutzer sie schliesst). Mit `false` meldet er nur das Neue.
 */
export function StehendeHinweise({ hinweise, onSchliessen }: StehendeHinweiseProps) {
  return (
    <div className="stehende-hinweise" role="status" aria-atomic="false">
      {hinweise.map((hinweis) => (
        <div key={hinweis.id} className="stehender-hinweis">
          <span>{hinweis.text}</span>
          <button
            type="button"
            className="icon-btn"
            aria-label="Hinweis schließen"
            onClick={() => onSchliessen(hinweis.id)}
          >
            <Ikone name="x" />
          </button>
        </div>
      ))}
    </div>
  );
}
